/**
 * PropertyForm — multi-step Admin form to create / edit a Property with rich details.
 * Sections: Basics · Media · Trust & Legal · Rich Details · Review
 *
 * On FIRST property listing (per user/device), a mandatory Declaration & Disclaimer modal
 * is shown. Once acknowledged, `ebg_listing_ack_v1` is stored in localStorage.
 */
import React, { useState } from "react";
import { X, ArrowRight, ArrowLeft, Plus, Trash2, Check, Home as HomeIcon, Image as ImageIcon, ShieldCheck, FileText, ClipboardCheck, ScrollText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ImageUploader, GalleryUploader } from "@/components/ImageUploaders";

const LISTING_ACK_KEY = "ebg_listing_ack_v1";

const DECLARATION_POINTS = [
    "I/We are the lawful owner or duly authorized representative of the property being listed.",
    "All information, documents, photographs, approvals, permissions, and details submitted by me/us are true, accurate, complete, and up to date.",
    "I/We shall be solely responsible for the authenticity, legality, ownership, title, approvals, encumbrances, and any claims or disputes relating to the property.",
    "EBG Realty acts only as a technology-enabled real estate listing and marketing platform. Verification conducted by EBG Realty is based solely on the documents and information provided by the property owner or authorized representative and should not be construed as a legal opinion, guarantee, or certification of title.",
    "EBG Realty shall not be liable for any dispute, litigation, fraud, misrepresentation, title defect, financial claim, government action, or any loss arising from the property listing or transaction.",
    "I/We agree to indemnify and hold harmless EBG Realty, its directors, employees, affiliates, and representatives from any claims, damages, liabilities, costs, or legal proceedings arising out of incorrect information, false declarations, or disputes relating to the property.",
    "I/We understand that EBG Realty reserves the right to reject, suspend, or remove any property listing without prior notice if any information is found to be false, misleading, incomplete, or in violation of applicable laws or platform policies.",
];

const STATUSES = ["Ready to Move", "Under Construction", "New Launch"];
const TYPES = ["Apartment", "Villa", "Penthouse", "Plot", "Land", "Commercial"];
const AUCTION_TYPES = ["Bank Auction (SARFAESI)", "DRT Auction", "Government / Authority Auction", "Court Auction"];
const POSSESSION = ["Physical", "Symbolic"];
const LEGAL = ["verified", "in_progress", "not_started"];
const RISK = ["low", "moderate", "high"];
const POI_CATS = ["school", "hospital", "metro", "mall", "airport", "road"];

const TRUST_KEYS = [
    ["legal_verification",     "Legal Verification"],
    ["builder_reputation",     "Builder Reputation"],
    ["construction_quality",   "Construction Quality"],
    ["delivery_track_record",  "Delivery Track Record"],
    ["appreciation_potential", "Appreciation Potential"],
    ["location_connectivity",  "Location & Connectivity"],
    ["livability",             "Livability"],
    ["documentation_status",   "Documentation Status"],
];

const EMPTY = {
    title: "", developer: "", city: "", locality: "", country: "India", type: "Apartment",
    status: "Ready to Move", listing_type: "sale", price: 1, price_display: "", currency: "INR",
    bhk: 3, area_sqft: 2000, bathrooms: 3, parking: 2,
    description: "", highlights: [], amenities: [],
    images: [], cover_image: "",
    featured: false, address: "", lat: null, lng: null,
    rera_number: "", legal_status: "verified", builder_authorized: true, price_match_assured: true,
    trust_score: { total: 0, legal_verification: 0, builder_reputation: 0, construction_quality: 0, delivery_track_record: 0, appreciation_potential: 0, location_connectivity: 0, livability: 0, documentation_status: 0 },
    investment_score: 75, rental_yield_pct: 3.2, appreciation_pct_yoy: 8.5, risk_level: "low",
    video_tour_url: "", drone_video_url: "", brochure_url: "",
    floor_plans: [], payment_plan: [], construction_updates: [],
    pros: [], cons: [], faqs: [], nearby_pois: [],
    builder_profile: null,
    price_per_sqft: null, possession_date: "",
    auction_date: "", auction_time: "", auction_emd: "", auction_emd_deadline: "", auction_inspection_date: "",
    auction_authority: "", auction_type: AUCTION_TYPES[0], auction_possession: "Physical",
    auction_notice_ref: "", auction_source_url: "", is_sample: false,
    is_dealer: false, land_category: "", land_size: null, land_size_unit: "Sq. Ft.", approval_details: "", development_potential: "", investment_info: "",
};
const LAND_CATS = ["Residential Land", "Commercial Land", "Agricultural / Farm Land", "Plots", "Development Land", "Investment Land"];
const LAND_UNITS = ["Sq. Ft.", "Sq. Yards", "Acres", "Cents", "Hectares"];

const STEPS = [
    { k: "basics",  l: "Basics",        Icon: HomeIcon },
    { k: "media",   l: "Media",         Icon: ImageIcon },
    { k: "trust",   l: "Trust & Legal", Icon: ShieldCheck },
    { k: "rich",    l: "Rich Details",  Icon: FileText },
    { k: "review",  l: "Review",        Icon: ClipboardCheck },
];

// merge existing property data with EMPTY defaults
const buildInitial = (existing, preset) => {
    if (!existing) return { ...EMPTY, ...(preset || {}) };
    const merged = { ...EMPTY, ...existing };
    merged.trust_score = { ...EMPTY.trust_score, ...(existing.trust_score || {}) };
    return merged;
};

export default function PropertyForm({ existing, preset, onClose, onSaved }) {
    const [f, setF]     = useState(() => buildInitial(existing, preset));
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    // Show mandatory declaration only for NEW listings when the user hasn't acknowledged before
    const [declared, setDeclared] = useState(() => !!existing?.id || !!localStorage.getItem(LISTING_ACK_KEY));

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
    const setTrust = (key, val) => setF((p) => {
        const ts = { ...p.trust_score, [key]: Number(val) };
        // auto-calc total as average of 8 factors
        const factors = TRUST_KEYS.map(([k]) => Number(ts[k]) || 0);
        const total = Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
        return { ...p, trust_score: { ...ts, total } };
    });

    const save = async () => {
        setBusy(true);
        try {
            // ensure numeric fields properly typed & required non-empty
            const payload = {
                ...f,
                land_category: ["Land", "Plot"].includes(f.type) ? (f.land_category || LAND_CATS[0]) : (f.land_category || null),
                land_size_unit: f.land_size_unit || null,
                price: Number(f.price) || 1,
                bhk: f.bhk ? Number(f.bhk) : null,
                area_sqft: f.area_sqft ? Number(f.area_sqft) : null,
                bathrooms: f.bathrooms ? Number(f.bathrooms) : null,
                parking: f.parking ? Number(f.parking) : null,
                price_per_sqft: f.price_per_sqft ? Number(f.price_per_sqft) : null,
                investment_score: Number(f.investment_score) || 0,
                rental_yield_pct: f.rental_yield_pct ? Number(f.rental_yield_pct) : null,
                appreciation_pct_yoy: f.appreciation_pct_yoy ? Number(f.appreciation_pct_yoy) : null,
                cover_image: f.cover_image || (f.images && f.images[0]) || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80",
                price_display: f.price_display || (f.currency === "INR" ? `₹${f.price} Cr` : `AED ${f.price}M`),
            };
            if (existing?.id) {
                await api.put(`/admin/properties/${existing.id}`, payload);
                toast.success("Property updated");
            } else {
                await api.post("/admin/properties", payload);
                toast.success("Property created");
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    // Gate: show declaration first if unacknowledged
    if (!declared) {
        return (
            <ListingDeclarationModal
                onCancel={onClose}
                onAgree={() => { localStorage.setItem(LISTING_ACK_KEY, new Date().toISOString()); setDeclared(true); }}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-6" data-testid="property-form-modal">
            <div className="bg-white w-full md:max-w-5xl md:max-h-[92vh] flex flex-col shadow-2xl">
                {/* Header */}
                <header className="flex items-center justify-between px-6 py-4 border-b border-black/10 shrink-0">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">
                            {existing ? "Edit Property" : "New Property"}
                        </div>
                        <h2 className="font-serif text-2xl mt-1">{f.title || "Untitled listing"}</h2>
                    </div>
                    <button onClick={onClose} data-testid="property-form-close" className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white flex items-center justify-center">
                        <X size={15}/>
                    </button>
                </header>

                {/* Step tabs */}
                <nav className="flex items-center gap-1 px-4 py-3 border-b border-black/8 overflow-x-auto shrink-0">
                    {STEPS.map((s, i) => (
                        <button
                            key={s.k}
                            data-testid={`step-${s.k}`}
                            onClick={() => setStep(i)}
                            className={`flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold whitespace-nowrap ${i === step ? "bg-black text-white" : i < step ? "text-[var(--ebg-gold)]" : "text-black/40 hover:text-black"}`}
                        >
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${i === step ? "bg-[var(--ebg-gold)] text-black" : i < step ? "bg-[var(--ebg-gold)] text-black" : "bg-black/8 text-black/50"}`}>
                                {i < step ? <Check size={10}/> : i + 1}
                            </span>
                            {s.l}
                        </button>
                    ))}
                </nav>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0" data-testid={`step-body-${STEPS[step].k}`}>
                    {step === 0 && <BasicsStep f={f} set={set}/>}
                    {step === 1 && <MediaStep f={f} set={set}/>}
                    {step === 2 && <TrustStep f={f} set={set} setTrust={setTrust}/>}
                    {step === 3 && <RichStep f={f} set={set}/>}
                    {step === 4 && <ReviewStep f={f}/>}
                </div>

                {/* Footer */}
                <footer className="flex items-center justify-between px-6 py-4 border-t border-black/10 bg-black/2 shrink-0">
                    <button
                        disabled={step === 0}
                        onClick={prev}
                        data-testid="prop-form-prev"
                        className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/60 hover:text-black disabled:opacity-30 flex items-center gap-2"
                    >
                        <ArrowLeft size={13}/> Back
                    </button>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/40">Step {step + 1} of {STEPS.length}</div>
                    {step < STEPS.length - 1 ? (
                        <button onClick={next} data-testid="prop-form-next" className="bg-[var(--ebg-gold)] text-black px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] flex items-center gap-2">
                            Next <ArrowRight size={13}/>
                        </button>
                    ) : (
                        <button disabled={busy || !f.title || !f.city || !f.locality || !f.description} onClick={save} data-testid="prop-form-save" className="bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black disabled:opacity-50 flex items-center gap-2">
                            {busy ? "Saving…" : (existing ? "Update Property" : "Publish Property")} <Check size={13}/>
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
}

// -------- Steps --------
function BasicsStep({ f, set }) {
    return (
        <div className="grid md:grid-cols-2 gap-4">
            <Text label="Title *" v={f.title} onChange={(v) => set({ title: v })} testid="pf-title"/>
            <Text label="Developer" v={f.developer || ""} onChange={(v) => set({ developer: v })} testid="pf-developer"/>
            <Text label="City *" v={f.city} onChange={(v) => set({ city: v })} testid="pf-city"/>
            <Text label="Locality *" v={f.locality} onChange={(v) => set({ locality: v })} testid="pf-locality"/>
            <Select label="Country" v={f.country} onChange={(v) => set({ country: v })} opts={["India", "UAE"]}/>
            <Select label="Type" v={f.type} onChange={(v) => set({ type: v })} opts={TYPES} testid="pf-type"/>
            <Select label="Status" v={f.status} onChange={(v) => set({ status: v })} opts={STATUSES}/>
            <Select label="Listing Type" v={f.listing_type} onChange={(v) => set({ listing_type: v })} opts={["sale", "rent", "auction"]} testid="pf-listing-type"/>
            <Num label={f.listing_type === "auction" ? "Reserve Price (₹ Cr)" : "Price (₹ Cr / AED M)"} v={f.price} onChange={(v) => set({ price: v })} testid="pf-price"/>
            <Text label="Price Display" v={f.price_display} onChange={(v) => set({ price_display: v })} placeholder="₹ 5.25 Cr" testid="pf-price-display"/>
            <Select label="Currency" v={f.currency} onChange={(v) => set({ currency: v })} opts={["INR", "AED"]}/>
            <Num label="Price / sqft" v={f.price_per_sqft || ""} onChange={(v) => set({ price_per_sqft: v })}/>
            <Num label="BHK" v={f.bhk || ""} onChange={(v) => set({ bhk: v })}/>
            <Num label="Area (sq.ft)" v={f.area_sqft || ""} onChange={(v) => set({ area_sqft: v })}/>
            <Num label="Bathrooms" v={f.bathrooms || ""} onChange={(v) => set({ bathrooms: v })}/>
            <Num label="Parking" v={f.parking || ""} onChange={(v) => set({ parking: v })}/>
            <Text label="Possession Date" v={f.possession_date || ""} onChange={(v) => set({ possession_date: v })} placeholder="Dec 2027"/>
            <Text label="Address" v={f.address || ""} onChange={(v) => set({ address: v })}/>
            {f.listing_type === "auction" && <AuctionFields f={f} set={set}/>}
            {["Land", "Plot"].includes(f.type) && f.listing_type !== "auction" && (
                <div className="md:col-span-2 border-2 border-[var(--ebg-gold)]/40 bg-[var(--ebg-gold)]/5 p-5" data-testid="pf-land-block">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-bold mb-4">Land Details</div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Select label="Land Category" v={f.land_category || LAND_CATS[0]} onChange={(v) => set({ land_category: v })} opts={LAND_CATS} testid="pf-land-category"/>
                        <Num label="Land Size" v={f.land_size} onChange={(v) => set({ land_size: v })} testid="pf-land-size"/>
                        <Select label="Size Unit" v={f.land_size_unit || "Sq. Ft."} onChange={(v) => set({ land_size_unit: v })} opts={LAND_UNITS} testid="pf-land-unit"/>
                        <div className="md:col-span-3"><Text label="Approval Details" v={f.approval_details} onChange={(v) => set({ approval_details: v })} testid="pf-land-approvals"/></div>
                        <div className="md:col-span-3"><Text label="Development Potential" v={f.development_potential} onChange={(v) => set({ development_potential: v })} testid="pf-land-potential"/></div>
                        <div className="md:col-span-3"><Text label="Investment Information" v={f.investment_info} onChange={(v) => set({ investment_info: v })} testid="pf-land-investment"/></div>
                    </div>
                    <p className="text-[11px] text-black/55 mt-3">RERA number (Trust step) is optional for land &mdash; fill it only where applicable.</p>
                </div>
            )}
            <label className="md:col-span-2 flex items-center gap-2 text-[12px] font-bold">
                <input type="checkbox" checked={!!f.is_dealer} onChange={(e) => set({ is_dealer: e.target.checked })} data-testid="pf-is-dealer"/>
                Dealer Property (shows a &ldquo;Dealer Property&rdquo; tag; enquiries still go to EBG)
            </label>
            <div className="md:col-span-2">
                <TextArea label="Description *" v={f.description} onChange={(v) => set({ description: v })} rows={4} testid="pf-description"/>
            </div>
            <div className="md:col-span-2">
                <ChipsInput label="Highlights" v={f.highlights} onChange={(v) => set({ highlights: v })}/>
            </div>
            <div className="md:col-span-2">
                <ChipsInput label="Amenities" v={f.amenities} onChange={(v) => set({ amenities: v })}/>
            </div>
            <label className="md:col-span-2 flex items-start gap-3 mt-2 p-4 border-2 border-[var(--ebg-gold)]/40 bg-[var(--ebg-gold)]/5 cursor-pointer hover:border-[var(--ebg-gold)] transition-colors" data-testid="pf-featured-block">
                <input type="checkbox" checked={!!f.featured} onChange={(e) => set({ featured: e.target.checked })} className="mt-1 w-4 h-4 accent-[var(--ebg-gold)]" data-testid="pf-featured-checkbox"/>
                <div>
                    <div className="text-[12px] uppercase tracking-[0.18em] font-bold text-black">{f.listing_type === "auction" ? "Feature on Home page" : "Mark as Signature Project"}</div>
                    <div className="text-[11px] text-black/60 mt-1">{f.listing_type === "auction" ? "Featured auctions appear first in the \u201cAuction Properties\u201d section on the Home page." : "Signature Projects are showcased in the \u201cOur Signature Projects\u201d section on the Home page. Recommended: keep 4–8 projects marked as Signature at a time."}</div>
                </div>
            </label>
        </div>
    );
}

function MediaStep({ f, set }) {
    const endpoint = `/admin/uploads/image?folder=${f.listing_type === "auction" ? "auctions" : "properties"}`;
    return (
        <div className="space-y-6">
            <ImageUploader label="Cover Image *" value={f.cover_image} onChange={(v) => set({ cover_image: v })} testid="pf-cover-uploader" endpoint={endpoint} hint="Landscape, at least 1600×900px. JPG/PNG/WEBP up to 8 MB."/>
            <GalleryUploader label="Gallery Images" values={f.images || []} onChange={(v) => set({ images: v })} testid="pf-gallery-uploader" endpoint={endpoint}/>
            <details className="border border-black/10 p-4">
                <summary className="text-[11px] uppercase tracking-[0.2em] font-bold cursor-pointer" data-testid="pf-url-toggle">Or paste image links instead</summary>
                <div className="space-y-4 mt-4">
                    <Text label="Cover Image URL" v={f.cover_image} onChange={(v) => set({ cover_image: v })} testid="pf-cover"/>
                    <ChipsInput label="Additional Image URLs" v={f.images} onChange={(v) => set({ images: v })} placeholder="Paste image URL and press Enter"/>
                </div>
            </details>
            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-black/8">
                <Text label="Video Tour URL"    v={f.video_tour_url || ""} onChange={(v) => set({ video_tour_url: v })}/>
                <Text label="Drone Video URL"   v={f.drone_video_url || ""} onChange={(v) => set({ drone_video_url: v })}/>
                <Text label="Brochure URL"      v={f.brochure_url || ""} onChange={(v) => set({ brochure_url: v })}/>
            </div>
        </div>
    );
}

function TrustStep({ f, set, setTrust }) {
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
                <Text label="RERA Number" v={f.rera_number || ""} onChange={(v) => set({ rera_number: v })} testid="pf-rera"/>
                <Select label="Legal Status" v={f.legal_status} onChange={(v) => set({ legal_status: v })} opts={LEGAL} testid="pf-legal-status"/>
                <Select label="Risk Level" v={f.risk_level} onChange={(v) => set({ risk_level: v })} opts={RISK}/>
                <label className="flex items-center gap-2 mt-4">
                    <input type="checkbox" checked={!!f.builder_authorized} onChange={(e) => set({ builder_authorized: e.target.checked })}/>
                    <span className="text-[12px] uppercase tracking-[0.18em] font-bold">Builder Authorised</span>
                </label>
                <label className="flex items-center gap-2 mt-4">
                    <input type="checkbox" checked={!!f.price_match_assured} onChange={(e) => set({ price_match_assured: e.target.checked })}/>
                    <span className="text-[12px] uppercase tracking-[0.18em] font-bold">Price-Match Assured</span>
                </label>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                <Num label="Investment Score (0-100)" v={f.investment_score} onChange={(v) => set({ investment_score: v })}/>
                <Num label="Rental Yield %" v={f.rental_yield_pct || ""} onChange={(v) => set({ rental_yield_pct: v })}/>
                <Num label="Appreciation % YoY" v={f.appreciation_pct_yoy || ""} onChange={(v) => set({ appreciation_pct_yoy: v })}/>
            </div>
            {/* Trust factors */}
            <div className="border border-black/10 p-5 bg-[var(--ebg-ivory)]">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Trust Score</div>
                        <div className="font-serif text-2xl">Total: {f.trust_score.total}/100</div>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    {TRUST_KEYS.map(([k, l]) => (
                        <div key={k} data-testid={`trust-${k}`}>
                            <div className="flex items-center justify-between text-[12px] mb-1"><span className="font-bold">{l}</span><span className="tabular-nums text-black/60">{f.trust_score[k] || 0}</span></div>
                            <input type="range" min={0} max={100} value={f.trust_score[k] || 0} onChange={(e) => setTrust(k, e.target.value)} className="w-full accent-[var(--ebg-gold)]"/>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function RichStep({ f, set }) {
    return (
        <div className="space-y-6">
            {/* Pros/Cons */}
            <div className="grid md:grid-cols-2 gap-4">
                <ChipsInput label="Pros" v={f.pros} onChange={(v) => set({ pros: v })}/>
                <ChipsInput label="Cons" v={f.cons} onChange={(v) => set({ cons: v })}/>
            </div>

            {/* Floor Plans */}
            <RowList
                label="Floor Plans"
                items={f.floor_plans}
                empty={{ config: "3 BHK - Type A", area_sqft: 2000, image: "" }}
                onChange={(v) => set({ floor_plans: v })}
                render={(row, patch) => (
                    <>
                        <Text label="Configuration" v={row.config} onChange={(v) => patch({ config: v })}/>
                        <Num  label="Area (sqft)" v={row.area_sqft} onChange={(v) => patch({ area_sqft: v })}/>
                        <Text label="Image URL"  v={row.image} onChange={(v) => patch({ image: v })}/>
                    </>
                )}
            />

            {/* Payment Plan */}
            <RowList
                label="Payment Plan"
                items={f.payment_plan}
                empty={{ milestone: "On Booking", percentage: 10 }}
                onChange={(v) => set({ payment_plan: v })}
                render={(row, patch) => (
                    <>
                        <Text label="Milestone" v={row.milestone} onChange={(v) => patch({ milestone: v })}/>
                        <Num  label="Percentage" v={row.percentage} onChange={(v) => patch({ percentage: v })}/>
                    </>
                )}
            />

            {/* POIs */}
            <RowList
                label="Nearby POIs"
                items={f.nearby_pois}
                empty={{ name: "Metro Station", category: "metro", distance_km: 1.2 }}
                onChange={(v) => set({ nearby_pois: v })}
                render={(row, patch) => (
                    <>
                        <Text label="Name" v={row.name} onChange={(v) => patch({ name: v })}/>
                        <Select label="Category" v={row.category} onChange={(v) => patch({ category: v })} opts={POI_CATS}/>
                        <Num label="Distance (km)" v={row.distance_km} onChange={(v) => patch({ distance_km: v })}/>
                    </>
                )}
            />

            {/* FAQs */}
            <RowList
                label="FAQs"
                items={f.faqs}
                empty={{ q: "", a: "" }}
                onChange={(v) => set({ faqs: v })}
                render={(row, patch) => (
                    <>
                        <Text label="Question" v={row.q} onChange={(v) => patch({ q: v })}/>
                        <TextArea label="Answer" v={row.a} onChange={(v) => patch({ a: v })} rows={2}/>
                    </>
                )}
            />
        </div>
    );
}

function ReviewStep({ f }) {
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-3">
                <Kv k="Title" v={f.title || "—"}/>
                <Kv k="City" v={`${f.locality || ""}, ${f.city || ""}`}/>
                <Kv k="Type" v={f.type}/>
                <Kv k="Status" v={f.status}/>
                <Kv k="Price" v={f.price_display || `₹${f.price} Cr`}/>
                <Kv k="Trust Score" v={`${f.trust_score.total}/100`}/>
                <Kv k="Legal Status" v={f.legal_status}/>
                <Kv k="RERA" v={f.rera_number || "—"}/>
                <Kv k="Signature Project" v={f.featured ? "Yes" : "No"}/>
            </div>
            <div className="grid md:grid-cols-4 gap-2">
                <Kv k="Floor Plans" v={f.floor_plans?.length || 0}/>
                <Kv k="Payment Milestones" v={f.payment_plan?.length || 0}/>
                <Kv k="POIs" v={f.nearby_pois?.length || 0}/>
                <Kv k="FAQs" v={f.faqs?.length || 0}/>
            </div>
            {f.cover_image && <img src={f.cover_image} alt="cover" className="w-full max-h-72 object-cover border border-black/10"/>}
            <div className="text-[12px] text-black/60 border border-dashed border-black/15 p-4">
                Click <b>Publish Property</b> to save this listing to the public portfolio.
                Users will see the trust score, legal badges, RERA number, floor plans, payment plan, POIs and FAQs on the property detail page.
            </div>
        </div>
    );
}

function AuctionFields({ f, set }) {
    return (
        <div className="md:col-span-2 border-2 border-[var(--ebg-gold)]/40 bg-[var(--ebg-gold)]/5 p-5" data-testid="pf-auction-block">
            <div className="text-[11px] uppercase tracking-[0.24em] font-bold mb-4">Auction Details</div>
            <div className="grid md:grid-cols-3 gap-4">
                <DateField label="Auction Date *" v={f.auction_date} onChange={(v) => set({ auction_date: v })} testid="pf-auction-date"/>
                <Text label="Auction Time" v={f.auction_time} onChange={(v) => set({ auction_time: v })} placeholder="11:00 AM – 1:00 PM" testid="pf-auction-time"/>
                <Text label="EMD Amount" v={f.auction_emd} onChange={(v) => set({ auction_emd: v })} placeholder="₹14.2 L" testid="pf-auction-emd"/>
                <DateField label="EMD Last Date" v={f.auction_emd_deadline} onChange={(v) => set({ auction_emd_deadline: v })} testid="pf-auction-emd-deadline"/>
                <DateField label="Inspection Date" v={f.auction_inspection_date} onChange={(v) => set({ auction_inspection_date: v })} testid="pf-auction-inspection"/>
                <Text label="Bank / Authority *" v={f.auction_authority} onChange={(v) => set({ auction_authority: v })} testid="pf-auction-authority"/>
                <Select label="Auction Type" v={f.auction_type || AUCTION_TYPES[0]} onChange={(v) => set({ auction_type: v })} opts={AUCTION_TYPES} testid="pf-auction-type"/>
                <Select label="Possession" v={f.auction_possession || "Physical"} onChange={(v) => set({ auction_possession: v })} opts={POSSESSION} testid="pf-auction-possession"/>
                <Text label="Notice Reference No." v={f.auction_notice_ref} onChange={(v) => set({ auction_notice_ref: v })} testid="pf-auction-notice"/>
                <div className="md:col-span-3">
                    <Text label="Official Notice Link (bank / authority website)" v={f.auction_source_url} onChange={(v) => set({ auction_source_url: v })} placeholder="https://" testid="pf-auction-source"/>
                </div>
            </div>
            <label className="flex items-center gap-2 mt-4 text-[12px]">
                <input type="checkbox" checked={!!f.is_sample} onChange={(e) => set({ is_sample: e.target.checked })} data-testid="pf-is-sample"/>
                Show as a sample listing (adds a &ldquo;Sample&rdquo; label on the website)
            </label>
        </div>
    );
}

// -------- Inputs --------
function DateField({ label, v, onChange, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <input data-testid={testid} type="date" value={v || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}
function Text({ label, v, onChange, placeholder, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <input data-testid={testid} value={v || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}
function Num({ label, v, onChange, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <input data-testid={testid} type="number" step="any" value={v ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}
function Select({ label, v, onChange, opts, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <select data-testid={testid} value={v} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm bg-white outline-none focus:border-[var(--ebg-gold)]">
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </label>
    );
}
function TextArea({ label, v, onChange, rows = 3, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <textarea data-testid={testid} value={v || ""} rows={rows} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}
function ChipsInput({ label, v = [], onChange, placeholder = "Type and press Enter" }) {
    const [text, setText] = useState("");
    const add = () => {
        const t = text.trim();
        if (!t) return;
        onChange([...v, t]);
        setText("");
    };
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{label}</div>
            <div className="flex items-center gap-2 mb-2">
                <input value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className="flex-1 border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                <button type="button" onClick={add} className="border border-black/20 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-black hover:text-white flex items-center gap-1">
                    <Plus size={12}/> Add
                </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {v.map((chip, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-black px-2.5 py-1 text-[11px]">
                        {chip}
                        <button type="button" onClick={() => onChange(v.filter((_, j) => j !== i))} className="text-black/40 hover:text-red-600"><X size={11}/></button>
                    </span>
                ))}
            </div>
        </div>
    );
}
function RowList({ label, items = [], empty, onChange, render }) {
    const patch = (i, p) => {
        const next = items.map((row, j) => j === i ? { ...row, ...p } : row);
        onChange(next);
    };
    const remove = (i) => onChange(items.filter((_, j) => j !== i));
    const add    = () => onChange([...items, { ...empty }]);
    return (
        <div className="border border-black/10 p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.24em] font-bold">{label} ({items.length})</div>
                <button type="button" onClick={add} className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ebg-gold)] hover:text-black flex items-center gap-1">
                    <Plus size={12}/> Add
                </button>
            </div>
            {items.length === 0 && <div className="text-[12px] text-black/40 py-3">None yet.</div>}
            <div className="space-y-3">
                {items.map((row, i) => (
                    <div key={i} className="grid md:grid-cols-3 gap-3 border-b border-dashed border-black/10 pb-3 relative">
                        {render(row, (p) => patch(i, p))}
                        <button type="button" onClick={() => remove(i)} className="absolute -top-1 right-0 text-red-500 hover:text-red-700 text-[11px] font-bold flex items-center gap-1"><Trash2 size={12}/></button>
                    </div>
                ))}
            </div>
        </div>
    );
}
function Kv({ k, v }) {
    return (
        <div className="border border-black/10 px-3 py-2 bg-white">
            <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-black/45">{k}</div>
            <div className="text-[14px] font-bold mt-0.5 tabular-nums">{v}</div>
        </div>
    );
}

// -------- Property Listing Declaration --------
function ListingDeclarationModal({ onAgree, onCancel }) {
    const [checked, setChecked] = useState(false);
    return (
        <div className="fixed inset-0 z-[60] flex items-stretch md:items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-6" data-testid="listing-declaration-modal">
            <div className="bg-white w-full md:max-w-3xl md:max-h-[92vh] flex flex-col shadow-2xl">
                {/* Header */}
                <header className="flex items-start justify-between px-6 py-4 border-b border-black/10 shrink-0 bg-[var(--ebg-ivory)]">
                    <div className="flex items-start gap-3">
                        <span className="w-10 h-10 rounded-full bg-[var(--ebg-gold)]/15 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center mt-0.5">
                            <ScrollText size={17}/>
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">One-time Acknowledgement</div>
                            <h2 className="font-serif text-2xl mt-1">Property Listing Declaration &amp; Disclaimer</h2>
                            <div className="text-black/55 text-[12px] mt-1">Required before your first property listing on EBG Realty.</div>
                        </div>
                    </div>
                    <button onClick={onCancel} data-testid="ack-close" className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white flex items-center justify-center">
                        <X size={15}/>
                    </button>
                </header>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
                    <p className="text-[13px] text-black/75 leading-relaxed mb-5">
                        By registering and listing a property on the EBG Realty platform, I/We hereby declare and agree that:
                    </p>
                    <ol className="space-y-4">
                        {DECLARATION_POINTS.map((p, i) => (
                            <li key={i} className="flex gap-3" data-testid={`ack-point-${i + 1}`}>
                                <span className="font-serif text-[var(--ebg-gold)] text-lg leading-none mt-0.5 shrink-0 w-6">{i + 1}.</span>
                                <span className="text-[13px] leading-relaxed text-black/75">{p}</span>
                            </li>
                        ))}
                    </ol>

                    <label className="flex items-start gap-3 mt-8 pt-6 border-t border-black/10 cursor-pointer select-none" data-testid="ack-checkbox-wrap">
                        <span className={`w-5 h-5 border-2 flex items-center justify-center mt-0.5 shrink-0 transition-colors ${checked ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)]" : "border-black/30"}`}>
                            {checked && <Check size={13} className="text-black"/>}
                        </span>
                        <input type="checkbox" className="hidden" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="ack-checkbox"/>
                        <span className="text-[13px] text-black leading-relaxed">
                            I have read, understood, and agree to the above Terms, Declaration, and Disclaimer.
                        </span>
                    </label>
                </div>

                {/* Footer */}
                <footer className="flex items-center justify-between gap-3 px-6 py-4 border-t border-black/10 shrink-0 bg-black/2">
                    <button onClick={onCancel} data-testid="ack-cancel" className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/60 hover:text-black">
                        Cancel
                    </button>
                    <button
                        disabled={!checked}
                        onClick={onAgree}
                        data-testid="ack-agree"
                        className="bg-[var(--ebg-gold)] text-black px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        Agree &amp; Continue <ArrowRight size={13}/>
                    </button>
                </footer>
            </div>
        </div>
    );
}
